import { useState } from 'react';
import { X } from 'lucide-react';
import type { Studio, StudioId } from '@/domain/types';

interface Props {
  studios: Studio[];
  defaultStudioId: StudioId;
  onCancel: () => void;
  onCreate: (input: { firstName: string; lastName: string; studioId: StudioId }) => void;
}

export default function AddArtistModal({ studios, defaultStudioId, onCancel, onCreate }: Props) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [studioId, setStudioId] = useState<StudioId>(defaultStudioId);

  const canCreate = firstName.trim() !== '' && lastName.trim() !== '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-black/70 p-4"
      data-testid="artist-create-modal"
      onClick={onCancel}
    >
      <div
        className="panel p-6 w-full max-w-md space-y-4"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between">
          <div>
            <p className="label-small-caps text-gold-bright">New artist</p>
            <h2 className="heading-display text-2xl text-cream mt-1">Add to roster</h2>
          </div>
          <button type="button" onClick={onCancel} className="btn-quiet" aria-label="Close">
            <X size={14} />
          </button>
        </header>

        <label className="block text-[10px] font-inter uppercase tracking-widest text-cream-soft">
          First name
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoFocus
            className="mt-1 block w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
            data-testid="artist-create-firstName"
          />
        </label>

        <label className="block text-[10px] font-inter uppercase tracking-widest text-cream-soft">
          Last name
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="mt-1 block w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
            data-testid="artist-create-lastName"
          />
        </label>

        <label className="block text-[10px] font-inter uppercase tracking-widest text-cream-soft">
          Studio
          <select
            value={studioId}
            onChange={(e) => setStudioId(e.target.value as StudioId)}
            className="mt-1 block w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
            data-testid="artist-create-studio"
          >
            {studios.map((s) => (
              <option key={s.id} value={s.id}>
                {s.brand} · {s.name}
              </option>
            ))}
          </select>
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} className="btn-quiet text-xs">
            Cancel
          </button>
          <button
            type="button"
            disabled={!canCreate}
            onClick={() => onCreate({ firstName: firstName.trim(), lastName: lastName.trim(), studioId })}
            className="btn-primary text-xs"
            data-testid="artist-create-submit"
          >
            Create artist
          </button>
        </div>
      </div>
    </div>
  );
}
